// Feature constants - must match backend constants.py
export const FEATURES = {
  DASHBOARD: "dashboard",
  CHAT: "chat",
  INBOX: "inbox",
  INBOX_MONTHLY: "inbox-monthly",
  INBOX_WARMING: "inbox-warming",
  CONTENT_ENGINE: "content-engine",
  SMART_LIST: "smart-list",
  SEQUENCE: "sequence",
  IMAGE: "image",
  AUDIO: "audio",
  VIDEO: "video",
  WORKFLOW: "workflow",
  COLLABORATORS: "collaborators",
  INTEGRATIONS: "integrations",
  EMPLOYEE: "employee",
  KNOWLEDGE_BASE: "knowledge-base",
  CODE: "code",
  AGENT: "agent",
  PEOPLE_SEARCH: "people-search",
  COMPANY_SEARCH: "company-search",
  LINKEDIN_AUTOMATION: "linkedin-automation",
  LINKEDIN_POST_IMPORT: "linkedin-post-import",
  BROWSER_USE: "browser-task",
  EMAIL_VALIDATION: "email-validation",
  AUDIENCE_BUILDER: "audience-builder",
  PROMPT_STUDIO: "prompt-studio",
  TOOL_ROUTER: "tool-router",
  LANDING_PAGES: "landing-pages",
  PERSONAL_ASSISTANT: "personal-assistant",
  KEYWORD_MONITORING: "keyword-monitoring",
  MARKETING_CAMPAIGN_EMAIL: "marketing-campaign-email",
  APPS: "apps",
  APP_BUILDER: "app-builder",
} as const;

// Feature state constants
export const FEATURE_STATES = {
  ENABLED: "enabled", // Feature is fully accessible
  LOCKED: "locked", // Feature is visible but locked (upgrade required)
  HIDDEN: "hidden", // Feature is completely hidden
} as const;

export type FeatureName = (typeof FEATURES)[keyof typeof FEATURES];
export type FeatureState = (typeof FEATURE_STATES)[keyof typeof FEATURE_STATES];

// Feature access map type
export interface FeatureAccessMap {
  [key: string]: FeatureState;
}

// Feature display names for UI
export const FEATURE_DISPLAY_NAMES: { [key: string]: string } = {
  [FEATURES.DASHBOARD]: "Dashboard",
  [FEATURES.CHAT]: "Chat",
  [FEATURES.INBOX]: "Inbox",
  [FEATURES.INBOX_MONTHLY]: "Platform Inboxes",
  [FEATURES.INBOX_WARMING]: "Inbox Warming",
  [FEATURES.CONTENT_ENGINE]: "Content Engine",
  [FEATURES.SMART_LIST]: "Smart Lists",
  [FEATURES.SEQUENCE]: "Sequences",
  [FEATURES.IMAGE]: "Image Generation",
  [FEATURES.AUDIO]: "Audio Generation",
  [FEATURES.VIDEO]: "Video Generation",
  [FEATURES.WORKFLOW]: "Workflows",
  [FEATURES.COLLABORATORS]: "Collaborators",
  [FEATURES.INTEGRATIONS]: "Integrations",
  [FEATURES.EMPLOYEE]: "Employees",
  [FEATURES.KNOWLEDGE_BASE]: "Knowledge Base",
  [FEATURES.CODE]: "Code",
  [FEATURES.AGENT]: "Agents",
  [FEATURES.PEOPLE_SEARCH]: "People Search",
  [FEATURES.COMPANY_SEARCH]: "Company Search",
  [FEATURES.LINKEDIN_AUTOMATION]: "LinkedIn Automation",
  [FEATURES.LINKEDIN_POST_IMPORT]: "LinkedIn Post Import",
  [FEATURES.BROWSER_USE]: "Browser Tasks",
  [FEATURES.EMAIL_VALIDATION]: "Email Validation",
  [FEATURES.AUDIENCE_BUILDER]: "Audience Builder",
  [FEATURES.PROMPT_STUDIO]: "Prompt Studio",
  [FEATURES.TOOL_ROUTER]: "Tool Router",
  [FEATURES.LANDING_PAGES]: "Landing Pages",
  [FEATURES.PERSONAL_ASSISTANT]: "Executive Assistant",
  [FEATURES.KEYWORD_MONITORING]: "Keyword Monitoring",
  [FEATURES.MARKETING_CAMPAIGN_EMAIL]: "Marketing Campaigns",
  [FEATURES.APPS]: "Apps",
};

// Get all feature keys as array
export const getAllFeatures = (): FeatureName[] => {
  return Object.values(FEATURES);
};

// Get feature display name
export const getFeatureDisplayName = (
  feature: FeatureName,
  employeeName: string
): string => {
  if (FEATURES.EMPLOYEE === feature) {
    return employeeName;
  }
  return FEATURE_DISPLAY_NAMES[feature] || feature;
};

// Selectable features for member access control
// Ordered to match the Dashboard menu structure
export const SELECTABLE_FEATURES: FeatureName[] = [
  // Analytics
  FEATURES.DASHBOARD,

  // Core features
  FEATURES.CHAT,
  FEATURES.EMPLOYEE,
  FEATURES.KNOWLEDGE_BASE,

  // Main menu features
  FEATURES.SMART_LIST,
  FEATURES.SEQUENCE,
  FEATURES.INBOX,
  FEATURES.CONTENT_ENGINE,
  FEATURES.AGENT,

  // Content Tools submenu
  FEATURES.IMAGE,
  FEATURES.AUDIO,
  FEATURES.VIDEO,

  // Feature flag features (only shown if enabled for company)
  FEATURES.AUDIENCE_BUILDER,

  // Excluded from member selection:
  // - FEATURES.WORKFLOW (controlled via feature flag, not selectable)
  // - FEATURES.COLLABORATORS (admin-only)
  // - FEATURES.INTEGRATIONS (admin-only)
  // - FEATURES.CODE (super user only)
  // - FEATURES.PEOPLE_SEARCH (backend-only, no frontend UI)
  // - FEATURES.COMPANY_SEARCH (backend-only, no frontend UI)
  // - FEATURES.LINKEDIN_AUTOMATION (backend-only, no frontend UI)
  // - FEATURES.LINKEDIN_POST_IMPORT (backend-only, no frontend UI)
  // - FEATURES.BROWSER_USE (backend-only, no frontend UI)
];
